import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import SEO from "../components/SEO";
import SignaturePad from "../components/SignaturePad";
import { API } from "../lib/api";
import { LOGO_URL } from "../lib/brand";

// Sections requiring individual customer initials — MUST stay in-sync with authorizations.py.
const INITIALS_SECTIONS_ORDER = [
  "inspection_repair",
  "disassembly_diagnostic",
  "vehicle_movement",
  "storage_terms",
  "payment_responsibility",
  "risk_of_loss",
  "owner_supplied_parts",
  "paint_custom",
  "lien_collection",
  "electronic_signature",
];

const INSURANCE_COMM_OPTIONS = [
  ["speak_adjuster", "Speak with the assigned adjuster on my behalf"],
  ["request_supplement", "Request supplemental payments the insurer owes"],
  ["obtain_records", "Obtain and share estimate, documentation, and photos"],
  ["negotiate_scope", "Negotiate scope, procedures, and repair operations"],
  ["release_payment", "Discuss and request release of payment(s)"],
];

function CurrencyOrDash({ value }) {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function InitialsInput({ id, value, onChange }) {
  return (
    <input
      type="text"
      maxLength={4}
      required
      value={value || ""}
      onChange={(e) => onChange(e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))}
      placeholder="INIT"
      data-testid={`sign-initials-${id}`}
      className="w-20 uppercase text-center bg-ink-950 border border-ink-700 focus:border-proform-red focus:ring-0 font-mono tracking-[0.3em] text-chrome-100 py-2 px-2"
    />
  );
}

export default function SignAuthorization() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [status, setStatus] = useState({ signing: false, error: null, done: null });

  // Form state
  const [legalName, setLegalName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [signerPhone, setSignerPhone] = useState("");
  const [initials, setInitials] = useState({});
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [electronicRecordsConsent, setElectronicRecordsConsent] = useState(false);
  const [ownership, setOwnership] = useState(false);
  const [storageAck, setStorageAck] = useState(false);
  const [personalPropertyRemoved, setPersonalPropertyRemoved] = useState(false);
  const [operationalPhoto, setOperationalPhoto] = useState(true); // per spec, required
  const [marketingPhoto, setMarketingPhoto] = useState(false);
  const [insCommAuthorized, setInsCommAuthorized] = useState(false);
  const [insCommSelections, setInsCommSelections] = useState([]);
  const [authLevelConfirmed, setAuthLevelConfirmed] = useState(false);
  const [finalConfirmed, setFinalConfirmed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/sign/${token}`);
        if (cancelled) return;
        setState({ loading: false, error: null, data });
        // Prefill signer contact from snapshot
        if (data?.snapshot?.customer) {
          setSignerEmail(data.snapshot.customer.email || "");
          setSignerPhone(data.snapshot.customer.phone || "");
          const fn = data.snapshot.customer.first_name || "";
          const ln = data.snapshot.customer.last_name || "";
          if (fn || ln) setLegalName(`${fn} ${ln}`.trim());
        }
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail || "This link is invalid or has expired.";
        setState({ loading: false, error: detail, data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const snap = state.data?.snapshot;
  const sections = state.data?.agreement_sections || [];

  const allInitialsFilled = useMemo(
    () => INITIALS_SECTIONS_ORDER.every((s) => (initials[s] || "").trim().length >= 2),
    [initials]
  );

  const canSubmit =
    !status.signing &&
    legalName.trim().length >= 3 &&
    signatureDataUrl &&
    electronicRecordsConsent &&
    ownership &&
    storageAck &&
    operationalPhoto &&
    authLevelConfirmed &&
    finalConfirmed &&
    allInitialsFilled;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus({ signing: true, error: null, done: null });
    try {
      const payload = {
        electronic_records_consent: electronicRecordsConsent,
        legal_name: legalName.trim(),
        signer_email: signerEmail.trim(),
        signer_phone: signerPhone.trim(),
        signature_data_url: signatureDataUrl,
        initials,
        authorization_level_confirmed: authLevelConfirmed,
        ownership_representation: ownership,
        storage_acknowledged: storageAck,
        personal_property_removed: personalPropertyRemoved,
        operational_photography_agreed: operationalPhoto,
        marketing_photo_release: marketingPhoto,
        insurance_comm_authorized: insCommAuthorized,
        insurance_comm_selections: insCommSelections,
        final_agreement_confirmed: finalConfirmed,
      };
      const { data } = await axios.post(`${API}/sign/${token}`, payload);
      setStatus({ signing: false, error: null, done: data });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not submit. Please try again.";
      setStatus({ signing: false, error: detail, done: null });
    }
  };

  const toggleInsSel = (key) => {
    setInsCommSelections((sel) => (sel.includes(key) ? sel.filter((k) => k !== key) : [...sel, key]));
  };

  // Loading + error states
  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-950 text-chrome-100">
        <SEO title="ProForm — Repair Authorization" description="Repair authorization signing" />
        <div className="label">Loading agreement…</div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-950 text-chrome-100 p-6">
        <SEO title="ProForm — Link Unavailable" description="Repair authorization signing" />
        <div className="max-w-md text-center border border-ink-700 p-8">
          <img src={LOGO_URL} alt="ProForm" className="h-10 mx-auto mb-4" />
          <div className="font-display text-3xl mb-3">Link Unavailable</div>
          <p className="text-chrome-300 mb-4">{state.error}</p>
          <p className="text-chrome-300 text-sm">Call or text ProForm at <a className="text-proform-red" href="tel:7208398046">720-839-8046</a> and we'll re-issue a signing link.</p>
        </div>
      </div>
    );
  }
  if (state.data?.already_signed || status.done) {
    const result = status.done || state.data;
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-950 text-chrome-100 p-6" data-testid="sign-done-screen">
        <SEO title="ProForm — Repair Authorization Signed" description="Repair authorization signed" />
        <div className="max-w-lg text-center border border-ink-700 p-10">
          <img src={LOGO_URL} alt="ProForm" className="h-10 mx-auto mb-4" />
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-proform-red mb-2">Signed &amp; Received</div>
          <div className="font-display text-4xl uppercase mb-4">Thank you.</div>
          <p className="text-chrome-300 mb-6">Your Vehicle Drop-Off &amp; Repair Authorization has been fully executed. A confirmation email is on its way to your inbox with a copy of the signed PDF.</p>
          <div className="border border-ink-700 p-4 text-sm">
            <div className="label">Agreement</div>
            <div className="font-mono text-proform-red">{result?.agreement_id}</div>
            {result?.signed_at_utc && (<>
              <div className="label mt-3">Signed at (UTC)</div>
              <div className="font-mono text-chrome-100">{result.signed_at_utc}</div>
            </>)}
          </div>
          <div className="mt-6">
            <Link to="/" className="label hover:text-proform-red">Back to ProForm →</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-950 text-chrome-100 pb-32">
      <SEO title="ProForm — Repair Authorization" description="Sign your Vehicle Drop-Off & Repair Authorization" />

      {/* Header */}
      <header className="border-b border-ink-700 bg-ink-950/95 sticky top-0 z-40 backdrop-blur">
        <div className="wrap flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="ProForm" className="h-8" />
            <span className="label hidden sm:inline">Repair Authorization</span>
          </div>
          <div className="label hidden md:block">Secure signing · SSL</div>
        </div>
      </header>

      <div className="wrap py-10 max-w-3xl">
        {/* Intro */}
        <div className="mb-8">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-proform-red mb-2">Vehicle Drop-Off &amp; Repair Authorization</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl leading-none mb-4">Review &amp; Sign</h1>
          <p className="text-chrome-300 max-w-2xl">Please take a moment to read the entire agreement. Provide your initials for each section, complete the acknowledgments, and sign at the bottom. If anything is incorrect, do not sign — call or text ProForm at <a href="tel:7208398046" className="text-proform-red">720-839-8046</a>.</p>
        </div>

        {state.data?.staff_message && (
          <div className="border-l-2 border-proform-red bg-ink-900 p-4 italic text-chrome-100 mb-8">
            <div className="label mb-1">A note from ProForm</div>
            {state.data.staff_message}
          </div>
        )}

        {/* Snapshot summary card */}
        {snap && (
          <div className="border border-ink-700 p-6 mb-8" data-testid="sign-snapshot-card">
            <div className="label mb-3">Agreement Summary</div>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div><div className="label">Customer</div><div>{snap.customer.first_name} {snap.customer.last_name}</div></div>
              <div><div className="label">Vehicle</div><div>{snap.vehicle.year} {snap.vehicle.make} {snap.vehicle.model}</div></div>
              <div><div className="label">Email · Phone</div><div className="break-all">{snap.customer.email} · {snap.customer.phone}</div></div>
              <div><div className="label">VIN · Plate</div><div className="font-mono text-xs">{snap.vehicle.vin || "—"} · {snap.vehicle.license_plate || "—"}</div></div>
              <div><div className="label">Insurance</div><div>{snap.insurance?.company || "—"}</div></div>
              <div><div className="label">Claim #</div><div>{snap.insurance?.claim_number || "—"}</div></div>
              <div><div className="label">Authorized amount</div><div className="text-proform-red font-mono"><CurrencyOrDash value={snap.authorized_amount} /></div></div>
              <div><div className="label">Deductible</div><div className="font-mono"><CurrencyOrDash value={snap.deductible_amount} /></div></div>
              <div><div className="label">Storage rate</div><div className="font-mono">${state.data.storage_rate_daily.toFixed(2)} / eligible day</div></div>
              <div><div className="label">Authorization Level</div><div>{(snap.authorization_level || "").replace(/_/g, " ")}</div></div>
              <div className="md:col-span-2"><div className="label">Authorized scope</div><div className="text-chrome-100 whitespace-pre-line">{snap.scope}</div></div>
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-8">
          {/* Sections with initials */}
          {sections.map((s, idx) => (
            <section key={s.id} data-testid={`sign-section-${s.id}`} className="border-t border-ink-700 pt-6">
              <div className="flex items-baseline justify-between gap-4 mb-2">
                <h2 className="font-display uppercase text-xl md:text-2xl">{idx + 1}. {s.title}</h2>
                <InitialsInput id={s.id} value={initials[s.id]} onChange={(v) => setInitials((prev) => ({ ...prev, [s.id]: v }))} />
              </div>
              <p className="text-chrome-300 text-sm md:text-base leading-relaxed whitespace-pre-line">{s.body}</p>
            </section>
          ))}

          {/* Acknowledgments */}
          <section className="border-t border-ink-700 pt-6">
            <h2 className="font-display uppercase text-xl md:text-2xl mb-4">Acknowledgments</h2>
            <div className="space-y-3">
              <Check id="auth-level-confirmed" checked={authLevelConfirmed} onChange={setAuthLevelConfirmed}
                label={<>I confirm the authorization level shown above (<span className="text-proform-red">{(snap?.authorization_level || "").replace(/_/g, " ")}</span>) is what I am authorizing.</>} />
              <Check id="ownership" checked={ownership} onChange={setOwnership}
                label={<>I represent that I am the owner of this vehicle or an authorized representative of the owner, and that I have authority to authorize the work described above.</>} />
              <Check id="storage-ack" checked={storageAck} onChange={setStorageAck}
                label={<>I have read and understand the Storage Fees section, including that storage may accrue at <span className="text-proform-red font-mono">${state.data?.storage_rate_daily?.toFixed(2)}</span> per eligible day, only as legally permitted.</>} />
              <Check id="personal-property" checked={personalPropertyRemoved} onChange={setPersonalPropertyRemoved}
                label={<>I understand personal property should be removed from the vehicle. ProForm will not inventory ordinary vehicle contents.</>} />
              <Check id="operational-photo" checked={operationalPhoto} onChange={setOperationalPhoto}
                label={<>I authorize ProForm to take operational photographs of the vehicle during repair for insurance, documentation, warranty, and quality-control purposes. <span className="text-chrome-300 text-xs">(Required)</span></>} />
              <Check id="marketing-photo" checked={marketingPhoto} onChange={setMarketingPhoto}
                label={<>Optional: I permit ProForm to use photos of my vehicle for marketing (website, social media). <span className="text-chrome-300 text-xs">(Optional — leave unchecked if you'd prefer we don't.)</span></>} />
            </div>
          </section>

          {/* Insurance communication authorization */}
          <section className="border-t border-ink-700 pt-6">
            <h2 className="font-display uppercase text-xl md:text-2xl mb-4">Insurance Communication</h2>
            <Check id="ins-comm-auth" checked={insCommAuthorized} onChange={setInsCommAuthorized}
              label={<>I authorize ProForm to communicate with my insurance company on my behalf regarding this claim.</>} />
            {insCommAuthorized && (
              <div className="mt-3 space-y-2 pl-6 border-l border-ink-700">
                <div className="label mb-1">Scope of authorization</div>
                {INSURANCE_COMM_OPTIONS.map(([key, label]) => (
                  <label key={key} className="flex items-start gap-2 text-sm text-chrome-100 cursor-pointer">
                    <input type="checkbox" className="mt-1 accent-proform-red" checked={insCommSelections.includes(key)}
                      onChange={() => toggleInsSel(key)} data-testid={`sign-ins-${key}`} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            )}
          </section>

          {/* Electronic records consent */}
          <section className="border-t border-ink-700 pt-6">
            <h2 className="font-display uppercase text-xl md:text-2xl mb-4">Electronic Records &amp; Signature Consent</h2>
            <p className="text-chrome-300 text-sm mb-3">By signing this agreement electronically I consent to the use of electronic records and electronic signatures for this agreement and related documents, in accordance with Colorado's Uniform Electronic Transactions Act and the federal ESIGN Act. I may request a paper copy at any time.</p>
            <Check id="e-records-consent" checked={electronicRecordsConsent} onChange={setElectronicRecordsConsent}
              label={<>I consent to sign this agreement electronically.</>} />
          </section>

          {/* Legal name + contact */}
          <section className="border-t border-ink-700 pt-6">
            <h2 className="font-display uppercase text-xl md:text-2xl mb-4">Signer Information</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label">Legal name *</label>
                <input required minLength={3} value={legalName} onChange={(e) => setLegalName(e.target.value)}
                  className="field" data-testid="sign-legal-name" placeholder="Type your full legal name" />
              </div>
              <div>
                <label className="label">Email</label>
                <input type="email" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)}
                  className="field" data-testid="sign-email" />
              </div>
              <div className="md:col-span-2">
                <label className="label">Phone</label>
                <input value={signerPhone} onChange={(e) => setSignerPhone(e.target.value)}
                  className="field" data-testid="sign-phone" />
              </div>
            </div>
          </section>

          {/* Signature */}
          <section className="border-t border-ink-700 pt-6">
            <h2 className="font-display uppercase text-xl md:text-2xl mb-2">Signature</h2>
            <p className="text-chrome-300 text-sm mb-3">Draw your signature below using mouse or finger.</p>
            <SignaturePad onChange={setSignatureDataUrl} testid="sign-signature-pad" />
            {!signatureDataUrl && <div className="label text-proform-red mt-2">Signature required</div>}
          </section>

          {/* Final confirmation */}
          <section className="border-t border-ink-700 pt-6">
            <div className="bg-ink-900 border border-proform-red/60 p-5">
              <Check id="final-confirm" checked={finalConfirmed} onChange={setFinalConfirmed}
                label={<><span className="text-proform-red font-semibold">I have read, understand, and agree to the entire Vehicle Drop-Off &amp; Repair Authorization above.</span> I intend my electronic signature to have the same legal effect as a handwritten signature.</>} />
            </div>
          </section>

          {status.error && (
            <div className="border border-proform-red bg-proform-red/10 text-proform-red p-4 text-sm" data-testid="sign-error">
              {status.error}
            </div>
          )}

          <button type="submit" disabled={!canSubmit}
            data-testid="sign-submit-btn"
            className="btn btn-primary w-full justify-center text-base py-4 disabled:opacity-50 disabled:cursor-not-allowed">
            {status.signing ? "Submitting…" : "Sign &amp; Fully Execute Authorization"}
          </button>
          <p className="text-center text-chrome-300 text-xs">By clicking above, you sign this Vehicle Drop-Off &amp; Repair Authorization and consent to receive a signed PDF copy by email.</p>
        </form>
      </div>
    </div>
  );
}

function Check({ id, checked, onChange, label }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer group" data-testid={`sign-check-${id}`}>
      <input type="checkbox" className="mt-1 h-5 w-5 accent-proform-red cursor-pointer"
        checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-sm md:text-base text-chrome-100 group-hover:text-white">{label}</span>
    </label>
  );
}
